import React, { FunctionComponent } from 'react';
import { Grid, Typography } from '@rbx/ui';
import { slugifyEndpoint } from '@rbx/docsite-utilities/dist/browser';
import OpenCloudAPIOperation from '@modules/openCloud/utils/interfaces/OpenCloudAPIOperation';
import StandardMarkdownNoMargin from '@modules/ui/markdown/components/StandardMarkdownNoMargin';
import ReturnsContainer from '../../returns/components/ReturnsContainer';
import ParameterTable from '../../parameters/components/ParameterTable';
import CodeSampleContainer from '../../codeSamples/components/CodeSampleContainer';
import PermissionsContainer from '../../permissions/components/PermissionsContainer';
import LimitsContainer from '../../limits/components/LimitsContainer';
import ColoredMethod from './ColoredMethod';

import useEndpointContainerStyles from './EndpointContainer.styles';
import EndpointTitle from './roas/EndpointTitle';

export interface EndpointContainerProps {
  path: string;
  operation: OpenCloudAPIOperation;
}

const EndpointContainer: FunctionComponent<React.PropsWithChildren<EndpointContainerProps>> = ({
  path,
  operation: {
    name,
    method,
    parameters,
    description,
    responses,
    codeSamples,
    security,
    rateLimits,
  },
}) => {
  const {
    classes: { descriptionContainer, pathContainer, sectionContainer },
  } = useEndpointContainerStyles();

  return (
    <Grid container direction='column' wrap='nowrap'>
      <EndpointTitle title={name} operationId={slugifyEndpoint(path, method)} headingVariant='h3' />
      <Grid className={pathContainer} item XSmall>
        <ColoredMethod method={method} />{' '}
        <Typography variant='code' color='secondary'>
          {path}
        </Typography>
      </Grid>
      {description && (
        <Grid className={descriptionContainer} item XSmall>
          <StandardMarkdownNoMargin>{description}</StandardMarkdownNoMargin>
        </Grid>
      )}
      {parameters.length > 0 && (
        <Grid className={sectionContainer} item XSmall>
          <ParameterTable parameters={parameters} />
        </Grid>
      )}
      {codeSamples.length > 0 && (
        <Grid className={sectionContainer} item XSmall>
          <CodeSampleContainer codeSamples={codeSamples} />
        </Grid>
      )}
      {responses.length > 0 && (
        <Grid item XSmall>
          <ReturnsContainer responses={responses} />
        </Grid>
      )}
      {security.length > 0 && (
        <Grid className={sectionContainer} item XSmall>
          <PermissionsContainer security={security} />
        </Grid>
      )}
      {rateLimits && (
        <Grid className={sectionContainer} item XSmall>
          <LimitsContainer rateLimits={rateLimits} />
        </Grid>
      )}
    </Grid>
  );
};

export default EndpointContainer;
